import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { createPaginatedEndpoint } from "utils/services.util";

import { emptySplitApi } from "./empty-split-api";
import type { PromptOutputFormat } from "./types/prompts.type";
import {
  TextTemplate,
  TextTemplateFilters,
  TextTemplateInput,
  TextTemplatesResponse
} from "./types/text-templates.type";

export const textTemplatesApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getTextTemplate: builder.query<
      TextTemplate,
      { id: number; query?: string }
    >({
      query: ({ id, query = "{*}" }) =>
        `/text-templates/${id}/?query=${query}&limit=300&ordering=name`,
      providesTags: ["TextTemplates"]
    }),
    getTextTemplates: builder.query<TextTemplatesResponse, TextTemplateFilters>(
      {
        query: ({ query = "{*}", limit = 300, offset = 0, ...filters }) =>
          `/text-templates/?${formatValidQueryParamUrlCommaSeparated({
            ...filters,
            limit,
            offset
          })}&query=${query}&ordering=name`,
        providesTags: ["TextTemplates"]
      }
    ),
    getTextTemplatesPaginated: createPaginatedEndpoint<
      TextTemplateFilters,
      TextTemplate[]
    >(builder, {
      queryFn: ({ query = "{*}", limit = 25, offset = 0, ...filters }) =>
        `/text-templates/?${formatValidQueryParamUrlCommaSeparated({
          ...filters,
          limit,
          offset
        })}&query=${query}&ordering=name`,
      providesTags: ["TextTemplates"],
      transformResponseFn: (response: TextTemplate[]) => response
    }),
    getTextTemplatesCount: builder.query<
      number,
      Omit<TextTemplateFilters, "query" | "limit" | "offset">
    >({
      query: (filters) =>
        `/text-templates/count/?${formatValidQueryParamUrlCommaSeparated({
          ...filters
        })}`,
      providesTags: ["TextTemplates"]
    }),
    addTextTemplate: builder.mutation<TextTemplate, TextTemplateInput>({
      query: (templateData) => ({
        url: "/text-templates/",
        method: "POST",
        body: templateData
      }),
      invalidatesTags: ["TextTemplates"]
    }),
    updateTextTemplate: builder.mutation<TextTemplate, TextTemplateInput>({
      query: ({ id, ...templateData }) => ({
        url: `/text-templates/${id}/`,
        method: "PATCH",
        body: templateData
      })
      // We don't need invalidation here because we manually update the specific Template. Otherwise, setPageItemDataState breaks
      // invalidatesTags: ["TextTemplates"]
    }),
    /** Same as updateTextTemplate but no tag invalidation - for preview flow to avoid refetch */
    patchTextTemplateForPreview: builder.mutation<
      TextTemplate,
      TextTemplateInput
    >({
      query: ({ id, ...templateData }) => ({
        url: `/text-templates/${id}/`,
        method: "PATCH",
        body: templateData
      })
    }),
    /** Same as addTextTemplate but no tag invalidation - for preview flow to avoid refetch */
    addTextTemplateForPreview: builder.mutation<
      TextTemplate,
      TextTemplateInput
    >({
      query: (templateData) => ({
        url: "/text-templates/",
        method: "POST",
        body: templateData
      })
    }),
    deleteTextTemplate: builder.mutation<string, number>({
      query: (id) => ({
        url: `/text-templates/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["TextTemplates"]
    }),
    autoCompleteTextTemplates: builder.mutation<
      string,
      {
        name: string;
        text: string;
        type?: number;
        clinic_id?: number;
        user_prompt?: string;
        format?: PromptOutputFormat;
      }
    >({
      query: (templateData) => ({
        url: "/text-templates/complete",
        method: "POST",
        body: templateData
      })
    })
  })
});

export const {
  useGetTextTemplateQuery,
  useLazyGetTextTemplateQuery,
  useGetTextTemplatesQuery,
  useLazyGetTextTemplatesQuery,
  useGetTextTemplatesPaginatedQuery,
  useGetTextTemplatesCountQuery,
  useAddTextTemplateMutation,
  useUpdateTextTemplateMutation,
  useDeleteTextTemplateMutation,
  useAutoCompleteTextTemplatesMutation,
  usePatchTextTemplateForPreviewMutation,
  useAddTextTemplateForPreviewMutation
} = textTemplatesApi;
